import {inject, Injectable} from '@angular/core';
import * as moment from "moment/moment";
import {LocalStorageService} from "./local-storage.service";
import {HttpErrorNotifyService} from "./http-error-notify.service";
import {removeRepeatedBrand} from "../shared/brand-name";

// Дата очікуваного надходження на упаковку: note - позначка «інший склад: …», якщо дата не зі складу доставки клієнта
export interface ArrivalExpect {
  expect: string;
  note: string;
}

// Склади, по яких get_arrivals_datas віддає дати (як suppliers у buildBuData)
const ARRIVAL_WAREHOUSE_NAMES: {[war: number]: string} = {
  1: 'Центр',
  7: 'Брожка',
};

@Injectable({
  providedIn: 'root'
})
export class GoodsService {
  localStorage = inject(LocalStorageService);
  private notify = inject(HttpErrorNotifyService);

  constructor() {
  }

  /**
   * basket_data/add_basket відмовляє зі статусом 200 і текстом у error - раніше кабінет це ігнорував:
   * товар не лягав у кошик, а користувач нічого не бачив. Тексти бека змішані (укр/рос/англ,
   * у catch - сирий Exception), тому показуємо власний текст за кожною відомою причиною.
   * Порожній error (не передано упаковку/склад, не обрано доставку) лишаємо без повідомлення, як і раніше.
   */
  notifyAddBasketError(data: { error?: string } | null) {
    if (!data || !data.error) {
      return;
    }

    // Ці коди ставить ядро до контролера - їх уже обробляє інтерцептор (вхід/блокування)
    if (['not_authorized', 'user_deleted', 'site_blocked'].includes(data.error)) {
      return;
    }

    if (data.error === 'wrong_count') {
      this.notify.show(
        'Товар не додано до кошика',
        'Вкажіть кількість - не менше 1 шт.'
      );
    } else if (data.error === 'out_of_stock') {
      this.notify.show(
        'Товар не додано до кошика',
        'Цей товар у постачальника вже розпродано. Оновіть сторінку, щоб побачити актуальні залишки.'
      );
    } else if (data.error === 'Подзаказные товары недоступны для белого учета.') {
      this.notify.show(
        'Товар не додано до кошика',
        'Товари під замовлення недоступні для замовлення від ФОП/ТОВ.'
      );
    } else if (data.error === 'Товар недоступний для замовлення.'
      || data.error === 'There is no available goods for order.'
      || data.error === 'Не получили товар для белого учета.') {
      this.notify.show(
        'Товар не додано до кошика',
        'Товар зараз недоступний для замовлення. Оновіть сторінку.'
      );
    } else {
      this.notify.show(
        'Товар не додано до кошика',
        'Спробуйте ще раз. Якщо помилка повториться - зверніться до менеджера.'
      );
    }
  }

  buildData(data, haveGoods = 0) {
    const suppliers = data.suppliers;
    const goods = data.goods;
    const delivery = data.delivery;
    const date = data.date;
    const out_from_war = data.out_from_war;
    const moving_between_wars = data.moving_between_wars;
    const notWorkingDays = data.deliveries_not_working_days_calendar;
    const currencies = data.currencies;

    const goodsData = [];
    const delivery_id = parseInt(this.localStorage.get('delivery_id'));
    const currency_id = this.localStorage.get('currencies');

    for (let sup of suppliers) {
      if (delivery[delivery_id]) {
        let deleted = false;
        const deliveryData = delivery[delivery_id];

        sup.expire = moment(date.trim(), "DD.MM.YYYY HH:mm:ss");

        if (sup.partner) {
          const days_moving_to_war = parseInt(sup['war_' + this.partnerTargetWar(delivery_id, out_from_war)]) || 0;

          sup.delivery_til_time = parseInt(sup.delivery_til_time);

          sup.expire = this.partnerExpire(date, days_moving_to_war, sup.delivery_til_time);

          // Склады партнёра: своя дата на каждый склад, считается один раз на поставщика (не на товар)
          this.buildPartnerWarehouses(sup, {date, delivery_id, out_from_war, moving_between_wars, deliveryData, notWorkingDays});
        } else {
          if (moving_between_wars && moving_between_wars[sup.war]) {
            if (moving_between_wars[sup.war]['deleted'] == 1) {
              deleted = true;
              sup.expire = 'немає можливості поставити';

            } else {
              moving_between_wars[sup.war]['days'] = moving_between_wars[sup.war]['days'] == 0 ? 1 : moving_between_wars[sup.war]['days'];

              sup.expire = moment(date, "DD-MM-YYYY").add(moving_between_wars[sup.war]['days'], 'days');
            }
          }
        }

        if (!deleted) {
          sup.expire = this.expireText(this.shiftToDeliveryDay(sup.expire, deliveryData, notWorkingDays), date);
        }
      }
    }

    if (goods.length) {
      for (let good of goods) {
        good.firstArticle = good.article.split('|||')[0];
        good.name = good.name.replaceAll('&nbsp;', ' ');
        good.name = good.name.replace('|', '(');
        good.name = good.name.replace('|', ')');
        // «MANN FILTER MANN-FILTER Фільтр мастил» -> «MANN-FILTER Фільтр мастил» (#855)
        good.name = removeRepeatedBrand(good.name);
        good.resultLastArticleGood = good.article.split('|||');
        good.resultLastArticleGood.splice(0, 1);
        good.lastArticles = undefined;
        good.waiting = String(good.waiting);
        if (good.resultLastArticleGood.length) {
          good.lastArticles = good.resultLastArticleGood;
        } else {
          good.resultLastArticleGood = [];
        }

        goodsData.push({
          name: good.name,
          article: good.article,
          cnt_in_pack: good.cnt_in_pack,
          id: good.id,
          inventorization: good.inventorization,
          litraj: good.litraj,
          photos: good.photos,
          unbottled: good.unbottled,
          firstArticle: good.firstArticle,
          resultLastArticleGood: good.resultLastArticleGood,
          lastArticles: good.lastArticles,
          goodsSuppliers: [],
          goodsSuppliersPartner: [],
          isset_params: good.isset_params,
          waiting: good.waiting,
        });

        for (let sup of suppliers) {
          if (good[sup.alias + '_currency_id'] == null) {
            continue;
          }

          if (good[sup.alias + '_turned_off'] == 1) {
            continue;
          }

          if (haveGoods == 1 && parseInt(good[sup.alias + '_count']) <= 0) {
            good[sup.alias + '_count'] = null;
          }

          const price = this.rowPrice(good, sup, currencies, currency_id);

          // Нет готовой цены с бэка — ячейка недоступна (см. rowPrice), сами не считаем
          if (price === null) {
            continue;
          }

          good[sup.alias + '_price'] = price;

          good[sup.alias + '_expire'] = sup.expire;

          good[sup.alias + '_count_basket'] = 1;

          if (sup.partner) {
            if (good[sup.alias + '_count'] <= 0) {
              continue;
            }

            const row = {
              price: good[sup.alias + '_price'],
              count: good[sup.alias + '_count'],
              expire: good[sup.alias + '_expire'],
              countBasket: good[sup.alias + '_count_basket'],
              turnOn: good[sup.alias + '_turn_on'],
              star: good[sup.alias + '_star'],
              supplier: sup.supplier,
              war: sup.war,
              alias: sup.alias,
              partner: sup.partner,
              // 1 — бэк отдал реальное название поставщика (закупщик/админ), 0 — обезличенное «Партнер»
              supplierReal: sup.supplier_real,
              id: sup.id,
              gid: good[sup.alias + '_gid'],
            };

            // Разбивка по складам партнёра: в строке — основной склад, остальные — в row.others
            this.applyPartnerStock(row, sup, good[sup.alias + '_stock']);

            goodsData[goodsData.length - 1].goodsSuppliersPartner.push(row);
          } else {
            goodsData[goodsData.length - 1].goodsSuppliers.push({
              price: good[sup.alias + '_price'],
              count: good[sup.alias + '_count'],
              expire: good[sup.alias + '_expire'],
              countBasket: good[sup.alias + '_count_basket'],
              turnOn: good[sup.alias + '_turn_on'],
              star: good[sup.alias + '_star'],
              supplier: sup.supplier,
              war: sup.war,
              alias: sup.alias,
              partner: sup.partner,
              id: sup.id,
              gid: good[sup.alias + '_gid'],
            })
          }
        }

        if (good.photos) {
          good.photos = JSON.parse(good.photos);
        }
      }
    }

    for (let goodData of goodsData) {
      if (goodData.goodsSuppliers && delivery_id === 7) {
        goodData.goodsSuppliers.sort((a) => {
          if (a.alias === "w7") {
            return -1;
          }

          return 0;
        });
      }

      if (goodData.goodsSuppliersPartner) {
        goodData.goodsSuppliersPartner.sort((a, b) => {
          const sortA = parseFloat(a.price);
          const sortB = parseFloat(b.price);

          if (sortA < sortB) {
            return -1;
          }
          if (sortA > sortB) {
            return 1;
          }

          return 0;
        });

        goodData.goodsSuppliers = [...goodData.goodsSuppliers, ...goodData.goodsSuppliersPartner];

        let showName: boolean = false;
        let showPackId: boolean = false;
        goodData.goodsSuppliers.forEach(g => {
          if (!showName && g.count !== null) {
            g.showName = true;
            showName = true;
          } else {
            g.showName = false;
          }
        });

        goodData.goodsSuppliers.forEach(g => {
          if (!showPackId && g.count !== null) {
            g.showPackId = true;
            showPackId = true;
          } else {
            g.showPackId = false;
          }
        });
      }

      delete goodData.goodsSuppliersPartner;
    }

    goodsData.sort((a, b) => {
      const nameA = String(a.name);
      const nameB = String(b.name);

      if (nameA < nameB) {
        return -1;
      }

      if (nameA > nameB) {
        return 1;
      }

      const sortA = parseFloat(a.litraj);
      const sortB = parseFloat(b.litraj);

      if (sortA < sortB) {
        return -1;
      }
      if (sortA > sortB) {
        return 1;
      }

      return 0;
    })

    return goodsData;
  }

  // Наш склад, к сроку до которого привязана дата партнёра (как goods_zakupolog на бэке):
  // на самовывозе с Брожки (доставка 7) — склад выпуска заказа, во всех остальных случаях — Центр.
  // На Новой Почте (доставка 3) бэк берёт Центр при настройке клиента «підзамовні тільки через центр»,
  // иначе — склад привязки поставщика; привязки (warehouse) в ответе кабинета нет, а в справочнике
  // поставщиков она по умолчанию Центр, поэтому считаем от war_1 — как и out_from_war для Новой Пошты.
  private partnerTargetWar(deliveryId, outFromWar) {
    return deliveryId == 3 ? 1 : (parseInt(outFromWar) || 1);
  }

  // Дата отгрузки партнёра: дни до склада выпуска и «замовити до» (та же логика, что была в buildData)
  private partnerExpire(date: string, daysMovingToWar: number, deliveryTilTime: number) {
    let expire = moment(date.trim(), "DD.MM.YYYY HH:mm:ss");

    if (deliveryTilTime > 0 && daysMovingToWar == 0 && parseInt(expire.format('H')) >= deliveryTilTime) {
      expire = expire.add(1, 'days');
    } else if (daysMovingToWar > 0) {
      expire = expire.add(daysMovingToWar, 'days');

      if (expire.weekday() == 6) {
        expire = expire.add(1, 'days');
      }
    }

    return expire;
  }

  // Сдвиг на ближайший день доставки клиента с учётом календаря выходных
  private shiftToDeliveryDay(expire, deliveryData, notWorkingDays) {
    for (let i = 1; i <= 10; i++) {
      const weekday = expire.weekday();

      if (deliveryData['day' + weekday] == true && (notWorkingDays == 0 || notWorkingDays.find(dt => dt.dt == expire.format('DD.MM.Y')) === undefined)) {
        break;
      }
      expire = moment(expire).add(1, 'days');
    }

    return expire;
  }

  // Текст колонки «Умова»: пусто, если отгрузка в день заказа
  private expireText(expire, date: string) {
    if (expire.format('DD.MM') == moment(date.trim(), "DD.MM.YYYY HH:mm:ss").format('DD.MM')) {
      return '';
    }

    return `Після замовлення на ${moment(expire).format('DD.MM')}`;
  }

  // Склады партнёра (suppliers[].warehouses с бэка): дата и «замовити до» по каждому складу.
  // delivery_days склада — срок до нашего центрального склада, в тех же днях, что и war_1 поставщика
  // (бэк для поставщика без складов так и заполняет фолбэк: delivery_days = days_to_war1).
  // Клиент отгружается не с Центра — сверху идёт срок перемещения Центр → его склад из настроек
  // (moving_between_wars, ключ — склад-источник; как для наших товаров, 0 дней считается за 1).
  // Настройки перемещения нет или оно закрыто — берём разницу сроков самого поставщика (war_N − war_1).
  private buildPartnerWarehouses(sup, ctx) {
    sup.whMain = null;
    sup.whList = [];

    if (!Array.isArray(sup.warehouses) || !sup.warehouses.length) {
      return;
    }

    const targetWar = this.partnerTargetWar(ctx.delivery_id, ctx.out_from_war);
    const moveDays = this.partnerMoveDays(sup, targetWar, ctx.moving_between_wars);
    const today = moment(ctx.date.trim(), "DD.MM.YYYY HH:mm:ss").format('DD.MM');

    for (const w of sup.warehouses) {
      const tilTime = parseInt(w.delivery_til_time) || 0;
      const days = (parseInt(w.delivery_days) || 0) + moveDays;
      const expire = this.shiftToDeliveryDay(this.partnerExpire(ctx.date, days, tilTime), ctx.deliveryData, ctx.notWorkingDays);

      const wh = {
        id: parseInt(w.id),
        name: w.name,
        is_main: parseInt(w.is_main) == 1,
        tilTime,
        expire: this.expireText(expire, ctx.date),
        expireAt: expire.valueOf(),
        expireDay: expire.format('DD.MM') == today ? 'сьогодні' : expire.format('DD.MM'),
      };

      sup.whList.push(wh);

      if (wh.is_main && !sup.whMain) {
        sup.whMain = wh;
      }
    }

    if (!sup.whMain) {
      sup.whMain = sup.whList[0];
    }
  }

  // Сколько дней добавить к сроку склада, если клиент отгружается не с Центра:
  // срок перемещения Центр → склад выпуска из настроек, иначе разница сроков самого поставщика.
  private partnerMoveDays(sup, targetWar, movingBetweenWars) {
    if (!targetWar || targetWar == 1) {
      return 0;
    }

    const moving = movingBetweenWars && movingBetweenWars[1];

    if (moving && moving['deleted'] != 1) {
      const days = parseInt(moving['days']) || 0;

      return days == 0 ? 1 : days;
    }

    return Math.max(0, (parseInt(sup['war_' + targetWar]) || 0) - (parseInt(sup.war_1) || 0));
  }

  // Разбивка остатка партнёра по складам (partner_{id}_stock = {warehouse_id: count}).
  // В строке — остаток и дата основного склада; остальные склады с остатком — в row.others по ближайшей дате.
  // Нет разбивки (или остаток только на неизвестных складах) — строка как раньше: общий count.
  private applyPartnerStock(row, sup, stock) {
    row.others = [];

    if (!sup.whMain || !stock || typeof stock !== 'object') {
      return;
    }

    const main = sup.whMain;
    const mainCount = parseInt(stock[main.id]) || 0;
    const others = [];

    for (const wh of sup.whList) {
      if (wh.id == main.id) {
        continue;
      }

      const count = parseInt(stock[wh.id]) || 0;

      if (count > 0) {
        others.push({
          id: wh.id,
          name: wh.name,
          count,
          expire: wh.expire,
          expireAt: wh.expireAt,
          expireDay: wh.expireDay,
          tilTime: wh.tilTime,
          countBasket: 1,
        });
      }
    }

    if (mainCount <= 0 && !others.length) {
      return;
    }

    others.sort((a, b) => a.expireAt - b.expireAt);

    const othersCount = others.reduce((sum, o) => sum + o.count, 0);

    row.count = mainCount;
    row.expire = main.expire;
    row.whName = main.name;
    row.sup_wh_id = main.id;
    row.others = others;
    row.othersEarliest = others.length ? others[0].expireDay : '';
    // Точные остатки больше 9 в кабинете не показываем (как в колонке «Наявність»)
    row.othersCountText = othersCount > 9 ? '>10' : String(othersCount);
    row.whExpanded = false;
  }

  /**
   * Цена ячейки строки товара (каталог, карточка и аналоги, виджет корзины) — одна точка для buildData
   * и menuService.buildBasketData. Цену считает только бэк (PriceService, та же, что в корзине): фронт
   * переводит готовую цену из EUR в валюту кабинета и больше ничего не считает — прежний расчёт по сырым
   * полям (наценки, фикс-цена, курс оферты) удалён (#394).
   * Склады — priceEUR строки. Поле price не берём: бэк считает его в валюте куки currency_id, а кабинет
   * держит валюту в localStorage.
   * Колонки партнёров (#393) — partner_{id}_price_eur, подзаказная ветка, как у строки корзины; 0 — бэк цену
   * не посчитал (сбой PriceService, нет курса оферты).
   * Нет готовой цены — null и предупреждение в консоль: ячейка недоступна, buildData её не показывает,
   * а виджет не берёт в сумму.
   */
  rowPrice(row, sup, currencies, currency_id): number | null {
    const isPartner = sup.partner != 0;
    const priceEUR = isPartner ? row[sup.alias + '_price_eur'] : row.priceEUR;
    const eur = priceEUR == null ? NaN : parseFloat(priceEUR);

    if (!isFinite(eur) || (isPartner && eur <= 0)) {
      console.warn('Нет готовой цены с бэка — ячейка недоступна', {
        pack_id: row.id ?? row.pack_id,
        alias: sup.alias,
        price_eur: priceEUR,
      });

      return null;
    }

    return this.servicePrice(eur, currencies, currency_id);
  }

  // Цена PriceService (EUR) в валюте кабинета.
  // Гривна — до ближайшей копейки через toFixed(2), ровно как итог виджета (menuService.buildBasketData,
  // toFixed(2) у вызывающих), страница корзины ((summa × курс).toFixed(2)) и заказ ((summa × rate).toFixed(2)):
  // они переводят сумму в EUR в гривну и округляют до ближайшей копейки, а не вверх. «Вверх до целой» (#379)
  // давало 1535 против 1534.07 в корзине, «вверх до копейки» — 2191.90 против 2191.89 (#451).
  // Доллар — вверх до цента, как было. В EUR бэк уже округлил до 0.01, повторный Math.ceil(x * 100)
  // из-за float-хвоста (0.07 * 100 = 7.000000000000001) накинул бы 0.01.
  private servicePrice(eur: number, currencies, currency_id): number {
    if (parseInt(currency_id) == 3) {
      return Math.round(eur * 100) / 100;
    }

    const sPrice = (eur * currencies[3].rate) / currencies[currency_id].rate;

    if (parseInt(currency_id) == 1) {
      // Не Math.round(x * 100) / 100: на ровной половине копейки (2.585 × 100 = 258.5 во float) он даст 2.59,
      // а toFixed(2) виджета и корзины — 2.58. Та же операция над тем же произведением — совпадение до копейки.
      return Number(sPrice.toFixed(2));
    }

    return Math.ceil(sPrice * 100) / 100;
  }

  getWaitingPackIds(goodsData) {
    const pack_ids: number[] = [];

    for (const good of goodsData) {
      if (good && good.waiting === '1') {
        pack_ids.push(Number(good.id));
      }
    }

    return pack_ids;
  }

  // get_arrivals_datas віддає по рядку на склади 1 і 7 - на товар беремо дату складу доставки клієнта:
  // Брожка (7) лише для самовивозу з Брожки (доставка 7), для решти доставок - Центр (1).
  // Раніше рядок складу 7 перетирав дату Центру в обраному та модалці очікуваних надходжень.
  // Якщо товар очікується лише на іншому складі - віддаємо його дату з позначкою складу (note),
  // інакше товар показувався як «очікується» без дати (#446, рішення Віталія).
  getArrivalsExpectByPack(data): {[packId: number]: ArrivalExpect} {
    const war = parseInt(this.localStorage.get('delivery_id')) === 7 ? 7 : 1;
    const expectByPack: {[packId: number]: ArrivalExpect} = {};

    // на помилці get_arrivals_datas відповідає success: false без data
    for (const row of (Array.isArray(data) ? data : [])) {
      const packId = Number(row[0].pack_id);
      const rowWar = Number(row[0].warehouse_id);
      const own = rowWar === war;
      const cur = expectByPack[packId];

      // Дата свого складу завжди переважає: чужа не перетирає ані свою, ані вже взяту чужу
      if (cur && (!cur.note || !own)) continue;

      expectByPack[packId] = {
        expect: row[0].expect,
        note: own ? '' : 'інший склад: ' + (ARRIVAL_WAREHOUSE_NAMES[rowWar] || rowWar),
      };
    }

    return expectByPack;
  }

  applyWaitingExpect(goodsData, data) {
    const waitingDateByWar = {};

    // на помилці get_arrivals_datas відповідає success: false без data - не падаємо, просто без дат
    for (const row of (Array.isArray(data) ? data : [])) {
      waitingDateByWar[row[0].pack_id + '_' + row[0].warehouse_id] = row[0].expect;
    }

    for (const good of goodsData) {
      if (!good || good.waiting !== '1') continue;

      if (good && good.waiting === '1') {
        if (Array.isArray(good.goodsSuppliers)) {
          for (const sup of good.goodsSuppliers) {
            const dtSup = waitingDateByWar[good.id + '_' + sup.war];
            sup.waiting_expect = dtSup ? moment(dtSup).format('DD.MM') : '';
          }
        }
      }
    }
  }

  buildBuData(data, haveGoods = 0) {
    const goods = data.goods;

    const goodsData = [];
    const suppliers = [
      {
        supplier: 'Центр',
        war: 1,
        alias: 'w1',
        partner: 0,
      },
      {
        supplier: 'Брожка',
        war: 7,
        alias: 'w7',
        partner: 0,
      },
    ];

    const delivery_id = parseInt(this.localStorage.get('delivery_id'));

    if (goods.length) {
      for (let good of goods) {
        good.firstArticle = good.article.split('|||')[0];
        good.name = good.name.replaceAll('&nbsp;', ' ');
        good.name = good.name.replace('|', '(');
        good.name = good.name.replace('|', ')');
        // «MANN FILTER MANN-FILTER Фільтр мастил» -> «MANN-FILTER Фільтр мастил» (#855)
        good.name = removeRepeatedBrand(good.name);
        good.resultLastArticleGood = good.article.split('|||');
        good.resultLastArticleGood.splice(0, 1);
        good.lastArticles = undefined;

        if (good.resultLastArticleGood.length) {
          good.lastArticles = good.resultLastArticleGood;
        } else {
          good.resultLastArticleGood = [];
        }

        goodsData.push({
          name: good.name,
          article: good.article,
          cnt_in_pack: good.cnt_in_pack,
          id: good.pack_id,
          inventorization: 0,
          litraj: good.liters,
          photos: null,
          unbottled: 0,
          firstArticle: good.firstArticle,
          resultLastArticleGood: good.resultLastArticleGood,
          lastArticles: good.lastArticles,
          goodsSuppliers: [],
          goodsSuppliersPartner: [],
          isset_params: '0',
          waiting: '0',
        });

        for (let sup of suppliers) {
          if (haveGoods == 1 && parseInt(good[sup.alias + '_count']) <= 0) {
            good[sup.alias + '_count'] = null;
          }

          good[sup.alias + '_price'] = good.price;
          good[sup.alias + '_expire'] = '';
          good[sup.alias + '_count_basket'] = 1;

          goodsData[goodsData.length - 1].goodsSuppliers.push({
            price: good[sup.alias + '_price'],
            count: good[sup.alias + '_count'],
            expire: good[sup.alias + '_expire'],
            countBasket: good[sup.alias + '_count_basket'],
            turnOn: false,
            star: false,
            supplier: sup.supplier,
            war: sup.war,
            alias: sup.alias,
            partner: sup.partner,
            id: '',
            gid: null,
          });
        }
      }
    }

    for (let goodData of goodsData) {
      if (goodData.goodsSuppliersPartner) {
        if (goodData.goodsSuppliers && delivery_id === 7) {
          goodData.goodsSuppliers.sort((a) => {
            if (a.alias === "w7") {
              return -1;
            }

            return 0;
          });
        }

        goodData.goodsSuppliers = [...goodData.goodsSuppliers, ...goodData.goodsSuppliersPartner];

        let showName: boolean = false;
        let showPackId: boolean = false;

        goodData.goodsSuppliers.forEach(g => {
          if (!showName && g.count !== null) {
            g.showName = true;
            showName = true;
          } else {
            g.showName = false;
          }
        });

        goodData.goodsSuppliers.forEach(g => {
          if (!showPackId && g.count !== null) {
            g.showPackId = true;
            showPackId = true;
          } else {
            g.showPackId = false;
          }
        });
      }

      delete goodData.goodsSuppliersPartner;
    }

    goodsData.sort((a, b) => {
      const nameA = String(a.name);
      const nameB = String(b.name);

      if (nameA < nameB) {
        return -1;
      }

      if (nameA > nameB) {
        return 1;
      }

      const sortA = parseFloat(a.litraj);
      const sortB = parseFloat(b.litraj);

      if (sortA < sortB) {
        return -1;
      }
      if (sortA > sortB) {
        return 1;
      }

      return 0;
    });

    return goodsData;
  }
}
